/**
 * PreJoinScreen Component
 *
 * Pre-join screen for Daily.co video calls that allows users to:
 * - Preview their camera
 * - Toggle camera on/off
 * - Toggle microphone on/off
 * before joining the actual call.
 */

import React, { useEffect, useRef, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Loader2,
  AlertTriangle,
  RefreshCw,
  ArrowLeft,
  User,
} from 'lucide-react'
import { useDailyPreJoin } from '@/hooks/useDailyPreJoin'
import { cn } from '@/lib/utils'

export interface PreJoinSettings {
  audioEnabled: boolean
  videoEnabled: boolean
}

export interface PreJoinScreenProps {
  onJoin: (settings: PreJoinSettings) => void
  onCancel?: () => void
  displayName?: string
  appointmentInfo?: {
    patientName?: string
    providerName?: string
    startTime?: string
  }
  isProvider?: boolean
}

// Video preview component
const VideoPreview: React.FC<{
  stream: MediaStream | null
  videoEnabled: boolean
  hasCameraDevice: boolean
  displayName?: string
}> = ({ stream, videoEnabled, hasCameraDevice, displayName }) => {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const videoElement = videoRef.current
    if (!videoElement) return

    if (stream) {
      videoElement.srcObject = stream
      videoElement.play().catch(err => {
        console.warn('[VideoPreview] Could not auto-play video:', err)
      })
    } else {
      videoElement.srcObject = null
    }
  }, [stream])

  const showPlaceholder = !videoEnabled || !hasCameraDevice || !stream

  return (
    <div className="relative w-full h-full bg-[#0a0f14]">
      {/* Video element */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={cn(
          "w-full h-full object-cover transition-opacity duration-300",
          showPlaceholder ? 'opacity-0' : 'opacity-100'
        )}
        style={{ transform: 'scaleX(-1)' }}
      />
      
      {/* Placeholder */}
      {showPlaceholder && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0a0f14]">
          <div className="w-20 h-20 rounded-full bg-[#1a2530] flex items-center justify-center border border-slate-700/50 mb-3">
            {!hasCameraDevice ? (
              <VideoOff className="h-8 w-8 text-slate-500" />
            ) : (
              <User className="h-10 w-10 text-slate-500" />
            )}
          </div>
          <p className="text-slate-500 text-sm">
            {!hasCameraDevice ? 'No camera detected' : 'Camera is off'}
          </p>
        </div>
      )}

      {/* Display name badge */}
      {displayName && (
        <div className="absolute bottom-3 left-3 bg-[#0a0f14]/90 backdrop-blur-sm px-3 py-1.5 rounded-lg">
          <span className="text-sm text-white font-medium">{displayName}</span>
        </div>
      )}
    </div>
  )
}

// Main component
const PreJoinScreen: React.FC<PreJoinScreenProps> = ({
  onJoin,
  onCancel,
  displayName,
  appointmentInfo,
  isProvider,
}) => {
  const {
    stream,
    audioEnabled,
    videoEnabled,
    isInitializing,
    isReady,
    error,
    hasCameraDevice,
    hasMicrophoneDevice,
    audioLevel,
    toggleAudio,
    toggleVideo,
    retry,
    destroy,
  } = useDailyPreJoin()

  const handleJoin = useCallback(() => {
    const settings: PreJoinSettings = {
      audioEnabled,
      videoEnabled,
    }
    destroy()
    onJoin(settings)
  }, [audioEnabled, videoEnabled, destroy, onJoin])

  const handleCancel = useCallback(() => {
    destroy()
    onCancel?.()
  }, [destroy, onCancel])

  // Loading state
  if (isInitializing) {
    return (
      <div className="h-full min-h-screen w-full bg-[#0a0f14] flex flex-col items-center justify-center px-6">
        <img src="/nimbus_logo_white.svg" alt="Nimbbus Providers" className="h-9 mb-10" />
        <Loader2 className="h-10 w-10 animate-spin text-brand-teal mb-4" />
        <p className="text-base text-white font-medium text-center">Preparing your camera...</p>
        <p className="text-sm text-slate-500 mt-2 text-center">
          Please allow camera and microphone access when prompted
        </p>
      </div>
    )
  }

  // Permission denied error
  if (error && error.type === 'permission-denied') {
    return (
      <div className="h-full min-h-screen w-full bg-[#0a0f14] flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-[#141c24] rounded-2xl border border-slate-700/50 p-6">
          <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
            <AlertTriangle className="h-7 w-7 text-red-400" />
            </div>
          <h2 className="text-lg font-semibold mb-2 text-white text-center">
              Permission Required
            </h2>
          <p className="text-slate-400 text-center text-sm mb-5">{error.message}</p>
            <div className="flex gap-3">
              {onCancel && (
                <Button
                  variant="outline"
                className="flex-1 h-10 border-slate-700 text-slate-300 hover:bg-[#1a2530] hover:text-white bg-transparent"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              )}
              <Button
              className="flex-1 h-10 bg-brand-teal hover:bg-brand-teal/90 text-white"
                onClick={retry}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
        </div>
      </div>
    )
  }

  // Other errors
  if (error && !isReady) {
    return (
      <div className="h-full min-h-screen w-full bg-[#0a0f14] flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-[#141c24] rounded-2xl border border-slate-700/50 p-6">
          <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
            <AlertTriangle className="h-7 w-7 text-amber-400" />
            </div>
          <h2 className="text-lg font-semibold mb-2 text-white text-center">
              Preview Unavailable
            </h2>
          <p className="text-slate-400 text-center text-sm mb-5">{error.message}</p>
          <div className="flex gap-2">
              {onCancel && (
                <Button
                  variant="outline"
                className="flex-1 h-10 border-slate-700 text-slate-300 hover:bg-[#1a2530] hover:text-white bg-transparent text-sm"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              )}
              <Button
              className="flex-1 h-10 bg-brand-teal hover:bg-brand-teal/90 text-white text-sm"
                onClick={retry}
              >
              Retry
              </Button>
              <Button
              className="flex-1 h-10 bg-brand-gold hover:bg-brand-gold/90 text-white text-sm"
                onClick={() => onJoin({ audioEnabled: true, videoEnabled: true })}
              >
                Join Anyway
              </Button>
            </div>
        </div>
      </div>
    )
  }

  // Ready state
  return (
    <div className="h-full min-h-screen w-full bg-[#0a0f14] flex items-center justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-xl my-auto">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src="/nimbus_logo_white.svg" alt="Nimbbus Providers" className="h-9" />
        </div>

        {/* Main Card */}
        <div className="bg-[#141c24] rounded-2xl border border-slate-700/40 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-5 text-center border-b border-slate-700/40">
            <h1 className="text-xl font-semibold text-white">
              Ready to Join?
            </h1>
            {appointmentInfo && (
              <p className="text-sm text-slate-400 mt-1">
                {isProvider
                  ? `Video visit with ${appointmentInfo.patientName || 'Patient'}`
                  : `Video visit with ${appointmentInfo.providerName || 'Provider'}`}
              </p>
            )}
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Camera Preview */}
            <div className="relative aspect-video rounded-xl overflow-hidden border border-slate-700/40">
              <VideoPreview
                stream={stream}
                videoEnabled={videoEnabled}
                hasCameraDevice={hasCameraDevice}
                displayName={displayName}
              />

              {/* Muted indicator overlay */}
              {!audioEnabled && (
                <div className="absolute top-3 right-3 bg-red-500 p-2 rounded-full">
                  <MicOff className="h-4 w-4 text-white" />
                </div>
              )}
            </div>

            {/* Device Controls */}
            <div className="flex items-start justify-center gap-6 sm:gap-10">
              {/* Microphone Toggle */}
              <div className="flex flex-col items-center">
                <div className="relative">
                  {/* Animated outer ring for audio detection */}
                  {audioEnabled && hasMicrophoneDevice && audioLevel > 15 && (
                    <div 
                      className="absolute inset-0 rounded-full border-2 border-brand-teal animate-ping"
                      style={{ 
                        animationDuration: '1s',
                        opacity: Math.min(audioLevel / 100, 0.5)
                      }} 
                    />
                  )}
                  {/* Static outer ring when active */}
                  {audioEnabled && hasMicrophoneDevice && (
                    <div 
                      className="absolute -inset-1 rounded-full border border-brand-teal/30 transition-opacity duration-200"
                      style={{ opacity: audioLevel > 10 ? 1 : 0.3 }}
                    />
                  )}
                  <button
                  onClick={toggleAudio}
                  disabled={!hasMicrophoneDevice}
                    className={cn(
                      "relative w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all duration-200",
                      audioEnabled
                        ? "bg-brand-teal text-white"
                        : "bg-[#1a2530] text-white border border-slate-600/50 hover:bg-[#243040]",
                      !hasMicrophoneDevice && "opacity-40 cursor-not-allowed"
                    )}
                >
                  {audioEnabled ? (
                      <Mic className="h-5 w-5 sm:h-6 sm:w-6" />
                  ) : (
                      <MicOff className="h-5 w-5 sm:h-6 sm:w-6" />
                  )}
                  </button>
                </div>
                <span className="text-xs text-slate-400 mt-2">
                  {!hasMicrophoneDevice
                    ? 'No mic'
                    : audioEnabled
                      ? 'Mic on'
                      : 'Mic off'}
                </span>
              </div>

              {/* Camera Toggle */}
              <div className="flex flex-col items-center">
                <div className="relative">
                  <button
                  onClick={toggleVideo}
                  disabled={!hasCameraDevice}
                    className={cn(
                      "relative w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all duration-200",
                      videoEnabled
                        ? "bg-brand-teal text-white"
                        : "bg-[#1a2530] text-white border border-slate-600/50 hover:bg-[#243040]",
                      !hasCameraDevice && "opacity-40 cursor-not-allowed"
                    )}
                >
                  {videoEnabled ? (
                      <Video className="h-5 w-5 sm:h-6 sm:w-6" />
                  ) : (
                      <VideoOff className="h-5 w-5 sm:h-6 sm:w-6" />
                  )}
                  </button>
                </div>
                <span className="text-xs text-slate-400 mt-2">
                  {!hasCameraDevice
                    ? 'No camera'
                    : videoEnabled
                      ? 'Camera on'
                      : 'Camera off'}
                </span>
              </div>
            </div>

            {/* Device warnings */}
            {(!hasCameraDevice || !hasMicrophoneDevice) && (
              <div className="flex items-start gap-3 p-3 bg-amber-500/10 rounded-xl border border-amber-500/20">
                <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-200/90">
                  {!hasCameraDevice && !hasMicrophoneDevice
                    ? 'No camera or microphone detected. You can still join the call.'
                    : !hasCameraDevice
                      ? 'No camera detected. You can join with audio only.'
                      : 'No microphone detected. You can join with video only.'}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              {onCancel && (
                <Button
                  variant="outline"
                  className="flex-1 h-11 border-slate-600/50 text-slate-300 hover:bg-[#1a2530] hover:text-white bg-transparent"
                  onClick={handleCancel}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              )}
              <Button
                className="flex-1 h-11 bg-brand-teal hover:bg-brand-teal/90 text-white font-medium"
                onClick={handleJoin}
              >
                <Video className="h-4 w-4 mr-2" />
                Join Call
              </Button>
            </div>

            {/* Tips */}
            <p className="text-xs text-center text-slate-500">
              Tip: Make sure you're in a well-lit, quiet space for the best experience
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PreJoinScreen
